import { useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { DashboardSpec } from "../dashboard/catalog"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { logger } from "@/lib/logger"

import { useCreateDashboard, useDashboards, useUpdateDashboard } from "../hooks/use-dashboards"
import { DashboardService } from "../services/dashboard-service"

type SaveDashboardDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  dashboardId: string | null
  spec: DashboardSpec | null
  onDashboardCreated?: (id: string, title: string, type: "personal" | "company") => void
  /**
   * Refresh refreshed spec (real SQL values lifted from inline `_sql`
   * hints) committed back into the live store after a successful save.
   * Mirrors the autosave path so manual "Spara" from chat mode no longer
   * leaves the user staring at `data: []` placeholders.
   */
  onRefreshedSpec?: (spec: DashboardSpec) => void
}

export function SaveDashboardDialog({
  open,
  onOpenChange,
  dashboardId,
  spec,
  onDashboardCreated,
  onRefreshedSpec,
}: SaveDashboardDialogProps) {
  const { t } = useTranslation()
  const { data: dashboards = [] } = useDashboards()
  const createDashboard = useCreateDashboard()
  const updateDashboard = useUpdateDashboard()
  const [title, setTitle] = useState(spec?.title ?? "")
  const [description, setDescription] = useState("")
  const [type, setType] = useState<"personal" | "company">("personal")
  const currentDashboard = useMemo(
    () => dashboards.find((dashboard) => dashboard.id === dashboardId) ?? null,
    [dashboards, dashboardId]
  )

  useEffect(() => {
    if (open) {
      setTitle(currentDashboard?.title ?? spec?.title ?? "")
      setDescription(currentDashboard?.description ?? "")
      setType(currentDashboard?.type ?? "personal")
    }
  }, [open, spec, currentDashboard])

  const handleSave = useCallback(async () => {
    if (!spec) return

    try {
      let savedId: string | null = null
      let bindingsExtracted = 0
      if (dashboardId) {
        const payload: {
          title?: string
          description?: string
          spec?: Record<string, unknown>
          type?: "personal" | "company"
          replace_query_bindings?: boolean
        } = {}

        if (title !== (currentDashboard?.title ?? "")) {
          payload.title = title
        }
        if (description !== (currentDashboard?.description ?? "")) {
          payload.description = description
        }
        if (type !== (currentDashboard?.type ?? "personal")) {
          payload.type = type
        }

        const currentSpecJson = JSON.stringify(currentDashboard?.spec ?? null)
        const nextSpecJson = JSON.stringify(spec)
        if (currentSpecJson !== nextSpecJson) {
          payload.spec = spec as Record<string, unknown>
          payload.replace_query_bindings = true
        }

        if (Object.keys(payload).length === 0) {
          onOpenChange(false)
          return
        }

        const updated = await updateDashboard.mutateAsync({
          dashboardId,
          payload,
        })
        savedId = dashboardId
        bindingsExtracted = updated.query_bindings?.length ?? 0
      } else {
        // First-time save from chat mode: default `update_on_load: true`
        // so subsequent reloads auto-execute the SQL bindings we're
        // about to lift out of the spec's inline `_sql` hints. Mirrors
        // the autosave path in dash-page.tsx — without this, the
        // dashboard renders with `data: []` placeholders until the user
        // manually clicks "Uppdatera data" or toggles the per-dashboard
        // "Uppdatera vid öppning" button.
        const created = await createDashboard.mutateAsync({
          title: title || t("dash.dashboard.untitled"),
          description,
          spec: spec as Record<string, unknown>,
          type,
          update_on_load: true,
        })
        onDashboardCreated?.(created.id, title || t("dash.dashboard.untitled"), type)
        savedId = created.id
        bindingsExtracted = created.query_bindings?.length ?? 0
      }
      toast.success(t("dash.dashboard.saved"))
      onOpenChange(false)

      // Run the freshly-extracted bindings against the connection so
      // the user sees real SQL values instead of the agent's `data: []`
      // placeholders. The chat-side autosave does the same; without
      // this, a manual Spara from a chat-generated dashboard appears
      // "stuck in AI mode" with 0 / Inga data until refresh.
      if (savedId && bindingsExtracted > 0) {
        try {
          const refreshed = await DashboardService.refreshDashboard(savedId)
          if (refreshed) {
            const refreshedSpec: DashboardSpec = {
              ...(refreshed.spec as DashboardSpec),
              queryBindings: (refreshed.query_bindings ??
                spec.queryBindings) as DashboardSpec["queryBindings"],
            }
            onRefreshedSpec?.(refreshedSpec)
          }
        } catch (err) {
          logger.warn("Auto-refresh after manual save failed", err as Error)
        }
      }
    } catch {
      toast.error(t("dash.dashboard.save_error"))
    }
  }, [
    spec,
    dashboardId,
    title,
    description,
    type,
    createDashboard,
    updateDashboard,
    t,
    onOpenChange,
    onDashboardCreated,
    onRefreshedSpec,
    currentDashboard,
  ])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("dash.dashboard.save")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="dashboard-title">{t("dash.dashboard.save_title")}</Label>
            <Input
              id="dashboard-title"
              placeholder={t("dash.dashboard.untitled")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="dashboard-description">{t("dash.dashboard.save_description")}</Label>
            <Textarea
              id="dashboard-description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label>{t("dash.dashboard.type")}</Label>
            <Select value={type} onValueChange={(v) => setType(v as "personal" | "company")}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="personal">{t("dash.dashboard.save_type_personal")}</SelectItem>
                <SelectItem value="company">{t("dash.dashboard.save_type_company")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={createDashboard.isPending || updateDashboard.isPending}
            onClick={handleSave}
          >
            {t("dash.dashboard.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
